// @ts-nocheck
import { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';

import { FHSelect, FHSmartTextarea } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { getRangeArray } from '../../../../../utils/common';
import {
  Themes,
  BackgroundTypes,
  MINIMUM_REQUIRED_SENTENCE_LENGTH,
} from '../../../../../constants';
import { GoalTypes } from '../common/constants/general';
import { TreatmentPlanGoalStatus } from './constants/general';
import { targetDescriptionBlacklist } from '../../../../../pages/constants';
import { FORTA_YELLOW } from '../../../../../utils/colors';
import BaselineAndMasteryCriteria from '../common/components/baselineAndMasteryCriteria';
import ErrorBox from '../common/components/errorBox';

import type { TreatmentPlanResourceVersion } from '../common/constants/general';
import type { ITreatmentPlanGoal, SelectValueType } from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface GoalDescriptionsProps {
  treatmentPlanId: number;
  goalIndex: number;
  goal: ITreatmentPlanGoal;
  errors: any;
  goalVersion: TreatmentPlanResourceVersion;
  onUpdateField: (key: string, value: any) => void;
}

function GoalDescriptions(props: GoalDescriptionsProps) {
  const { goal, errors, treatmentPlanId, goalVersion, goalIndex, onUpdateField } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [description, setDescription] = useState<string>(goal?.description || '');
  const [operationalDefinition, setOperationalDefinition] = useState<string>(
    goal?.operational_definition || '',
  );
  const [objective, setObjective] = useState<string>(goal?.objective || '');
  const [antecedentModifications, setAntecedentModifications] = useState<string>(
    goal?.antecedent_modifications || '',
  );
  const [consequenceModifications, setConsequenceModifications] = useState<string>(
    goal?.consequence_modifications || '',
  );
  const [errorState, setErrorState] = useState<any>(null);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    setErrorState(errors);
  }, [errors]);

  useEffect(() => {
    if (!goal) return;

    setIsEditable(isTpEditable && goal.is_active);
    setDescription(goal?.description || '');
    setOperationalDefinition(goal?.operational_definition || '');
    setObjective(goal?.objective || '');
    setAntecedentModifications(goal?.antecedent_modifications || '');
    setConsequenceModifications(goal?.consequence_modifications || '');
  }, [isTpEditable, goal]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Status
          </Typography>
          {goal?.is_editing ? (
            <FHSelect
              data-testid="goalStatusDropDown"
              sx={{ minWidth: 150 }}
              inputHeight={40}
              fullWidth
              displayEmpty
              colorTheme={Themes.Primary}
              backgroundType={BackgroundTypes.Colored}
              disabled={!isEditable}
              renderValue={
                !goal.status_at_creation ? () => <Typography>Select status</Typography> : undefined
              }
              options={Object.entries(TreatmentPlanGoalStatus).map(([label, value]) => ({
                value,
                label,
              }))}
              optionDataTestIdPrefix="goalStatusOption"
              value={goal.status_at_creation || ''}
              onUpdate={(value: SelectValueType) => onUpdateField('status_at_creation', value)}
              error={errors && errors['status_at_creation']}
            />
          ) : (
            <Typography align="left">
              {goal.status_at_creation
                ? Object.entries(TreatmentPlanGoalStatus).find(
                    ([, value]) => value === goal.status_at_creation,
                  )?.[0] || ''
                : ''}
            </Typography>
          )}
        </Grid>
        <Grid item xs={3}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Current level
          </Typography>
          {goal?.is_editing ? (
            <FHSelect
              data-testid="currentLevelDropDown"
              sx={{ minWidth: 150 }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              backgroundType={BackgroundTypes.Colored}
              fullWidth
              displayEmpty
              disabled={!isEditable}
              renderValue={
                goal.current_level_at_creation === ''
                  ? () => <Typography>Select level</Typography>
                  : undefined
              }
              options={getRangeArray(0, 100).map((num) => ({
                value: num,
                label: num,
              }))}
              value={
                goal.current_level_at_creation || goal.current_level_at_creation === 0
                  ? goal.current_level_at_creation
                  : ''
              }
              onUpdate={(value: SelectValueType) =>
                onUpdateField('current_level_at_creation', value)
              }
              error={errors && errors['current_level_at_creation']}
            />
          ) : (
            <Typography align="left">
              {goal.current_level_at_creation === null ? (
                <ErrorBox width={160} />
              ) : (
                goal.current_level_at_creation
              )}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Box mt={2}>
        <BaselineAndMasteryCriteria
          goal={goal}
          treatmentPlanId={treatmentPlanId}
          isEditable={isEditable}
          onUpdateField={onUpdateField}
          errors={errors}
          goalIndex={goalIndex}
          goalVersion={goalVersion}
        />
      </Box>
      {goal.goal_type === GoalTypes.BehaviorReduction ? (
        <Grid container spacing={3} mt={2} sx={{ wordBreak: 'break-word' }}>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Operational definition* (Make sure to include onset/offset)
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_operational_definition-${goal.id}`}
                data-testid="operationalDefinitionTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter operational definition"
                value={operationalDefinition}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  setOperationalDefinition(e.target.value);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      operational_definition: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      operational_definition: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  onUpdateField('operational_definition', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  onUpdateField('operational_definition', operationalDefinition);
                }}
                error={errorState && errorState['operational_definition']}
                helperText={
                  errorState && errorState['operational_definition']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{operationalDefinition}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Objective*
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_objective-${goal.id}`}
                data-testid="ObjectiveTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter objective"
                value={objective}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  setObjective(e.target.value);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      objective: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      objective: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  onUpdateField('objective', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  onUpdateField('objective', objective);
                }}
                error={errorState && errorState['objective']}
                helperText={
                  errorState && errorState['objective']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{objective}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Antecedent modifications*
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_antecedent_modifications-${goal.id}`}
                data-testid="antecedentModificationsTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter antecedent modifications"
                value={antecedentModifications}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  setAntecedentModifications(e.target.value);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      antecedent_modifications: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      antecedent_modifications: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  onUpdateField('antecedent_modifications', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  onUpdateField('antecedent_modifications', antecedentModifications);
                }}
                error={errorState && errorState['antecedent_modifications']}
                helperText={
                  errorState && errorState['antecedent_modifications']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{antecedentModifications}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
              Consequence modifications*
            </Typography>
            {goal?.is_editing ? (
              <FHSmartTextarea
                id={`goal_consequence_modifications-${goal.id}`}
                data-testid="consequenceModificationsTextField"
                colorTheme={Themes.Primary}
                coloredBackground
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                maxRows={4.5}
                minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
                disabled={!isEditable}
                expandable={isEditable}
                placeholder="Enter consequence modifications"
                value={consequenceModifications}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  setConsequenceModifications(e.target.value);
                  if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                    setErrorState((prev: any) => ({
                      ...prev,
                      consequence_modifications: true,
                    }));
                  } else {
                    setErrorState((prev: any) => ({
                      ...prev,
                      consequence_modifications: false,
                    }));
                  }
                }}
                onBlur={(e) => {
                  onUpdateField('consequence_modifications', e.target.value);
                }}
                onCloseExpandDialog={() => {
                  onUpdateField('consequence_modifications', consequenceModifications);
                }}
                error={errorState && errorState['consequence_modifications']}
                helperText={
                  errorState && errorState['consequence_modifications']
                    ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                    : ''
                }
              />
            ) : (
              <Typography align="left">{consequenceModifications}</Typography>
            )}
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
          <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
            Description
          </Typography>
          {goal?.is_editing ? (
            <FHSmartTextarea
              id={`goal_description-${goal.id}`}
              data-testid="descriptionTextField"
              colorTheme={Themes.Primary}
              coloredBackground
              hightlightOnDetection
              heightlightColour={FORTA_YELLOW}
              blackListedWordsPhrases={targetDescriptionBlacklist}
              maxRows={4.5}
              minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
              disabled={!isEditable}
              expandable={isEditable}
              placeholder="Enter description of goal"
              value={description}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setDescription(e.target.value);
                if (e.target.value.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
                  setErrorState((prev: any) => ({
                    ...prev,
                    description: true,
                  }));
                } else {
                  setErrorState((prev: any) => ({
                    ...prev,
                    description: false,
                  }));
                }
              }}
              onBlur={(e) => {
                onUpdateField('description', e.target.value);
              }}
              onCloseExpandDialog={() => {
                onUpdateField('description', description);
              }}
              error={errorState && errorState['description']}
              helperText={
                errorState && errorState['description']
                  ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                  : ''
              }
            />
          ) : (
            <Typography align="left">{description}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default GoalDescriptions;
